import type { TourDemoVariant } from "@/lib/product-tour/tour-steps";

type Props = {
  variant: TourDemoVariant;
};

/** Micro-animação CSS/SVG do passo final de um tour de página — ilustra "clicar → preencher →
 *  salvar" sem nunca abrir o diálogo de verdade (evita competir com o overlay/focus-trap do Radix
 *  Dialog). Puro código, sem asset/dependência nova — só @keyframes escopadas neste componente. */
export function TourDemoAnimation({ variant }: Props) {
  return (
    <div className="tour-demo-animation my-3 flex h-24 items-center justify-center overflow-hidden rounded-lg bg-zinc-50 dark:bg-muted/30">
      <style>{KEYFRAMES}</style>
      {variant === "fill-form" && <FillFormDemo />}
      {variant === "schedule" && <ScheduleDemo />}
      {variant === "send-flow" && <SendFlowDemo />}
      {variant === "decision" && <DecisionDemo />}
      {variant === "invite-link" && <InviteLinkDemo />}
      {variant === "list-add" && <ListAddDemo />}
    </div>
  );
}

const KEYFRAMES = `
@keyframes tourCursorClick { 0%,100% { transform: scale(1); } 50% { transform: scale(0.85); } }
@keyframes tourFieldFill { 0% { width: 0; } 40%,100% { width: 100%; } }
@keyframes tourFieldFillDelayed { 0%,45% { width: 0; } 85%,100% { width: 100%; } }
@keyframes tourCheckIn { 0%,70% { opacity: 0; transform: scale(0.5); } 100% { opacity: 1; transform: scale(1); } }
@keyframes tourClockHand { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@keyframes tourArrowMove { 0% { transform: translateX(-14px); opacity: 0; } 20% { opacity: 1; } 80% { opacity: 1; } 100% { transform: translateX(14px); opacity: 0; } }
@keyframes tourDecisionPulse { 0%,40% { opacity: 0.25; } 50%,90% { opacity: 1; } 100% { opacity: 0.25; } }
@keyframes tourLinkCopy { 0%,55% { opacity: 0; transform: translateY(4px); } 70%,100% { opacity: 1; transform: translateY(0); } }
`;

function CursorDot({ style }: { style?: React.CSSProperties }) {
  return (
    <span
      className="absolute h-3 w-3 rounded-full border-2 border-white bg-brand-600 shadow"
      style={{ animation: "tourCursorClick 1.6s ease-in-out infinite", ...style }}
    />
  );
}

function FillFormDemo() {
  return (
    <div className="relative flex w-40 flex-col gap-2">
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-white/10">
        <div
          className="h-full rounded-full bg-brand-500"
          style={{ animation: "tourFieldFill 2.2s ease-in-out infinite" }}
        />
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-white/10">
        <div
          className="h-full rounded-full bg-brand-500"
          style={{ animation: "tourFieldFillDelayed 2.2s ease-in-out infinite" }}
        />
      </div>
      <div
        className="mt-0.5 flex h-6 w-16 items-center justify-center self-end rounded-md bg-brand-600 text-[9px] font-semibold text-white"
        style={{ animation: "tourCheckIn 2.2s ease-in-out infinite" }}
      >
        Salvar
      </div>
    </div>
  );
}

function ScheduleDemo() {
  return (
    <div className="relative flex h-14 w-14 items-center justify-center rounded-full border-2 border-brand-300 dark:border-primary/40">
      <span className="absolute h-1.5 w-1.5 rounded-full bg-brand-600" />
      <span
        className="absolute h-5 w-[2px] origin-bottom rounded-full bg-brand-600"
        style={{ bottom: "50%", animation: "tourClockHand 3s linear infinite" }}
      />
      <span
        className="absolute h-3 w-[2px] origin-bottom rounded-full bg-brand-400"
        style={{ bottom: "50%", animation: "tourClockHand 8s linear infinite" }}
      />
    </div>
  );
}

function SendFlowDemo() {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-100 text-[9px] font-bold text-brand-700 dark:bg-primary/15 dark:text-primary">
        Clínica
      </div>
      <div className="relative h-4 w-10 overflow-hidden">
        <span
          className="absolute top-1/2 h-[2px] w-3 -translate-y-1/2 bg-brand-500"
          style={{ animation: "tourArrowMove 1.8s ease-in-out infinite" }}
        />
      </div>
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600 text-[9px] font-bold text-white">
        Lab
      </div>
    </div>
  );
}

function DecisionDemo() {
  return (
    <div className="flex items-center gap-4">
      <div
        className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15"
        style={{ animation: "tourDecisionPulse 2.4s ease-in-out infinite" }}
      >
        ✓
      </div>
      <div
        className="flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-red-500 dark:bg-red-500/15"
        style={{ animation: "tourDecisionPulse 2.4s ease-in-out infinite reverse" }}
      >
        ✕
      </div>
    </div>
  );
}

function InviteLinkDemo() {
  return (
    <div className="relative flex w-40 flex-col items-center gap-1.5">
      <div className="flex h-7 w-full items-center rounded-md border border-zinc-200 bg-white px-2 text-[9px] text-zinc-400 dark:border-border dark:bg-card">
        bookdental.app/convite/...
      </div>
      <div
        className="flex h-5 items-center gap-1 rounded-full bg-emerald-100 px-2 text-[9px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
        style={{ animation: "tourLinkCopy 2s ease-in-out infinite" }}
      >
        Link copiado
      </div>
    </div>
  );
}

function ListAddDemo() {
  return (
    <div className="relative flex w-32 flex-col gap-1.5">
      <div className="h-2 w-full rounded-full bg-zinc-200 dark:bg-white/10" />
      <div className="h-2 w-full rounded-full bg-zinc-200 dark:bg-white/10" />
      <div
        className="h-2 w-full rounded-full bg-brand-500"
        style={{ animation: "tourCheckIn 2.2s ease-in-out infinite" }}
      />
      <CursorDot style={{ right: -4, bottom: -2 }} />
    </div>
  );
}
